import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap, Shield, TrendingUp, Phone, Mail, MapPin, Settings, ShoppingCart, Radio } from 'lucide-react';
import Seo from './Seo';

const AboutUs = () => {
  useEffect(() => {
    // Scroll to hash if present
    if (window.location.hash) {
      setTimeout(() => {
        const id = window.location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <Seo
        title="About Us"
        description="Libsys Inc is an enterprise IT services and strategic staffing firm with offices in the US and India, serving clients across banking, insurance, healthcare, retail, energy, and telecom."
        path="/about-us"
      />
      {/* ══ WHO ARE WE ════════════════════════════════════════════════════ */}
      <section className="pt-8 pb-12 bg-slate-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image collage */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-2 gap-4"
          >
            <div className="space-y-4">
               <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200/50">
                  <img src="/team_collaboration.png" alt="Libsys team collaborating" className="w-full aspect-[4/5] object-cover" />
               </div>
               <div className="bg-primary rounded-2xl p-6 text-white shadow-xl shadow-primary/20">
                  <div className="font-black text-4xl">24+</div>
                  <div className="text-blue-200 text-sm font-semibold mt-1">Years of IT Excellence</div>
               </div>
            </div>
            <div className="space-y-4 pt-12">
               <div className="bg-secondary rounded-2xl p-6 text-white shadow-xl shadow-secondary/20">
                  <div className="font-black text-4xl">Global</div>
                  <div className="text-blue-100 text-sm font-semibold mt-1">Clients Served Globally</div>
               </div>
               <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200/50">
                  <img src="/dt_hero.png" alt="Digital transformation technology" className="w-full aspect-[4/5] object-cover" />
               </div>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <span className="text-strategic-tagline">Who We Are</span>
              <h1 className="text-h1 text-primary">
                Empowering Businesses with Unified IT Solutions
              </h1>
              <p className="text-slate-600 text-body">
                At Libsys Inc., we specialize in providing comprehensive and unified IT solutions that empower businesses to achieve their full potential. Our integrated approach ensures seamless connectivity, enhanced efficiency, and optimized performance across all facets of your organization.
              </p>
              <p className="text-slate-500 text-subtext">
                We bridge technical gaps by providing integrated solutions that align with enterprise goals. Our focus is on identifying and implementing the right technologies to drive optimal business outcomes, ensuring our clients always remain at the forefront of digital excellence.
              </p>
            </div>

            <div className="space-y-5">
              {[
                {
                  icon: <Zap className="w-5 h-5 text-secondary" />,
                  title: 'Streamlined Operations',
                  desc: 'Our unified IT solutions integrate disparate systems into a cohesive framework — eliminating redundancies, reducing operational costs, and enhancing productivity.',
                },
                {
                  icon: <Shield className="w-5 h-5 text-secondary" />,
                  title: 'Enhanced Data Security',
                  desc: 'We prioritize data protection with state-of-the-art cybersecurity measures, ensuring sensitive information is safeguarded and regulatory compliance is maintained.',
                },
                {
                  icon: <TrendingUp className="w-5 h-5 text-secondary" />,
                  title: 'Proven Track Record',
                  desc: 'With over 24 years of industry experience and a 98% client satisfaction rate, our results consistently speak for themselves.',
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="font-bold text-primary text-subtext mb-1">{item.title}</h4>
                    <p className="text-slate-500 text-subtext leading-relaxed font-medium">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══ WE ARE THE CHANGE (CTA) ════════════════════════════════════════ */}
      <section className="py-16 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_30%_50%,white_0,transparent_45%)] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-left space-y-4 mb-12 max-w-4xl">
            <span className="text-red-tagline block tracking-[0.1em] uppercase text-tiny font-black mb-2">Driving Transformation</span>
            <h2 className="text-h2 lg:text-[44px]">We Are The <span className="text-secondary italic">Change</span></h2>
            <p className="text-blue-100/70 text-body font-medium max-w-2xl leading-relaxed">
              Libsys Inc. empowers organizations to navigate complexity, embrace innovation, and achieve measurable results — at scale, on schedule, every time.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {[
              {
                icon: <Settings className="w-7 h-7 text-secondary" />,
                title: 'Innovation at Scale',
                desc: 'We enhance operational efficiency and reliability through advanced IT solutions.',
              },
              {
                icon: <ShoppingCart className="w-7 h-7 text-secondary" />,
                title: 'Retail Reinvented',
                desc: 'We provide robust IT infrastructure to optimize inventory management and customer experience.',
              },
              {
                icon: <Radio className="w-7 h-7 text-secondary" />,
                title: 'Connecting the Future',
                desc: 'Our expertise in IT support ensures seamless communication and network stability.',
              },
            ].map((card, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur-md hover:bg-white/10 transition-all duration-500 group">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                     <div className="text-secondary opacity-100">{card.icon}</div>
                  </div>
                  <h4 className="text-[19px] font-black leading-tight">{card.title}</h4>
                </div>
                <p className="text-blue-100/70 text-tiny leading-relaxed font-medium">{card.desc}</p>
              </div>
            ))}
          </div>

          <div id="contact" className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-white/10 pt-12 scroll-mt-32">
            {[
              {
                icon: <MapPin className="w-5 h-5" />,
                tag: 'Corporate Office',
                value: <>2020 Calamos Ct, Suite 200,<br />Naperville, IL 60563, USA</>,
                link: 'https://www.google.com/maps/search/?api=1&query=2020+Calamos+Ct+Suite+200+Naperville+IL+60563'
              },
              {
                icon: <Phone className="w-5 h-5" />,
                tag: 'Reach Us At',
                value: '630-799-1556',
                link: 'tel:630-799-1556'
              },
              {
                icon: <Mail className="w-5 h-5" />,
                tag: 'Email Support',
                value: 'Info@LibsysInc.Com',
                link: 'mailto:Info@LibsysInc.Com'
              }
            ].map((item, i) => (
              <a 
                key={i}
                href={item.link}
                target={item.link.startsWith('http') ? "_blank" : undefined}
                rel={item.link.startsWith('http') ? "noopener noreferrer" : undefined}
                className="flex items-start gap-4 text-left p-6 lg:p-7 rounded-2xl bg-gradient-to-br from-[#1b3d6c] to-[#162e51] border border-blue-400/20 hover:shadow-2xl transition-all duration-400 group/c"
              >
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center group-hover/c:bg-secondary transition-colors flex-shrink-0">
                  <div className="text-white">{item.icon}</div>
                </div>
                <div>
                  <h4 className="text-red-tagline mb-1 text-[10px] tracking-[0.15em]">{item.tag}</h4>
                  <p className="text-white text-[15px] font-bold leading-relaxed">{item.value}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutUs;
